import { useState } from 'react';
import type { AlertPrefs } from '@glyph/protocol-ts';
import { fmtDateTime, useAlertPrefs, useCan, useCommand, useIsLocalShell, useSettings } from '@glyph/core';
import { Button, Card, CardHeader, Note, SegmentedControl, SwitchRow, useToast } from '@glyph/ui';

/** Device-detected kinds an operator may mute for this unit. "offline" is never muted here: use Off or snooze. */
const MUTABLE_KINDS: { key: string; label: string }[] = [
  { key: 'audio_silent', label: 'Audio silent' },
  { key: 'deepgram_error', label: 'ASR link' },
  { key: 'renderer_down', label: 'Renderer' },
  { key: 'throttled', label: 'Throttled' },
  { key: 'disk_low', label: 'Disk low' },
  { key: 'update_failed', label: 'Update failed' },
  { key: 'ltc_lost', label: 'Timecode' },
];
const ARM_OPTIONS = [
  { value: 'always', label: 'Armed' },
  { value: 'event', label: 'Event only' },
  { value: 'off', label: 'Off' },
] as const;
const nextMorning = (hour = 8): string => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
  return d.toISOString();
};
const hoursFromNow = (h: number): string => new Date(Date.now() + h * 3600_000).toISOString();

/**
 * Notifications for ONE unit. Cloud: arm mode (always / only while the assigned event is live / off), snooze and
 * per-kind mutes, which gate email, webhook and push but never the in-app alert list. Both shells: the audio-silence
 * alert, off by default because sessions go quiet on purpose.
 */
export function NotificationsCard({ deviceId, dense, className }: { deviceId?: string; dense?: boolean; className?: string }) {
  const cloud = !useIsLocalShell();
  const canArm = useCan('alerts.ack');
  const prefs = useAlertPrefs(deviceId);
  const settings = useSettings(deviceId);
  const cmd = useCommand(deviceId);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const p: AlertPrefs | undefined = prefs.data;
  const audio = settings.data?.audio;
  const save = async (patch: Partial<AlertPrefs>) => {
    if (!p) return;
    setBusy(true);
    try {
      await prefs.save({ arm_mode: p.arm_mode, snooze_until: p.snooze_until, muted_kinds: p.muted_kinds, ...patch });
    } catch (e) {
      toast.push((e as Error).message || 'Could not save notification settings', 'bad');
    } finally {
      setBusy(false);
    }
  };
  const setSilence = async (on: boolean) => {
    try {
      await cmd.run('settings.patch', { patch: { audio: { silence_alert: on } } });
      void settings.refetch();
    } catch (e) {
      toast.push((e as Error).message || 'Could not save', 'bad');
    }
  };
  const snoozed = !!p?.snooze_until && new Date(p.snooze_until).getTime() > Date.now();
  const meta = !cloud ? undefined : !p ? '…' : p.arm_mode === 'off' ? 'OFF' : snoozed ? `SNOOZED · ${fmtDateTime(p.snooze_until).toUpperCase()}` : p.arm_mode === 'event' ? 'EVENT ONLY' : 'ARMED';
  const off = !p || p.arm_mode === 'off';
  return (
    <Card className={className}>
      <CardHeader dense={dense} title="Notifications" meta={meta} metaTone={meta === 'ARMED' ? 'ok' : meta === 'OFF' || snoozed ? 'warn' : undefined} />
      {cloud && p && (
        <>
          <div className="flex flex-col gap-2 border-t border-line px-4 py-3">
            <SegmentedControl fullWidth value={p.arm_mode} onChange={(v) => void save({ arm_mode: v, snooze_until: v === 'off' ? null : p.snooze_until })} options={ARM_OPTIONS.map((o) => ({ ...o, disabled: !canArm || busy }))} aria-label="Arm mode" />
            <span className="font-mono text-xs leading-[1.5] text-fg4">
              {p.arm_mode === 'event' ? 'Email, webhook and push only while the assigned show is live.' : p.arm_mode === 'off' ? 'Nothing is sent for this unit. The alert list still fills.' : 'One message when this unit goes offline, one when it is back. Other alerts only while it is connected.'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
            <span className="micro text-fg4">Snooze</span>
            <Button size="xs" mono disabled={!canArm || busy || off} onClick={() => void save({ snooze_until: hoursFromNow(2) })}>
              2 H
            </Button>
            <Button size="xs" mono disabled={!canArm || busy || off} onClick={() => void save({ snooze_until: hoursFromNow(12) })}>
              12 H
            </Button>
            <Button size="xs" mono disabled={!canArm || busy || off} onClick={() => void save({ snooze_until: nextMorning(8) })}>
              UNTIL 08:00
            </Button>
            {snoozed && (
              <Button size="xs" mono variant="ghost" disabled={!canArm || busy} onClick={() => void save({ snooze_until: null })}>
                CLEAR
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
            <span className="micro w-full text-fg4">Muted for this unit</span>
            {MUTABLE_KINDS.map((k) => {
              const on = p.muted_kinds.includes(k.key);
              return (
                <button
                  key={k.key}
                  type="button"
                  disabled={!canArm || busy}
                  aria-pressed={on}
                  onClick={() => void save({ muted_kinds: on ? p.muted_kinds.filter((x) => x !== k.key) : [...p.muted_kinds, k.key] })}
                  className={`rounded-md border px-2 py-1 font-mono text-xs font-medium ${on ? 'border-warn text-warn' : 'border-border2 text-fg3 hover:text-fg'}`}
                >
                  {on ? '✕ ' : ''}
                  {k.label}
                </button>
              );
            })}
          </div>
        </>
      )}
      {cloud && !p && prefs.isError && (
        <div className="border-t border-line px-4 py-3">
          <Note tone="warn">Notification settings are not available for this unit.</Note>
        </div>
      )}
      {audio && (
        <SwitchRow
          title="Audio silence alert"
          hint={audio.silence_alert ? `fires after ${audio.silence_alert_s} s below ${audio.silence_dbfs} dBFS while captioning` : 'off · sessions go quiet on purpose (workshops, breaks)'}
          checked={!!audio.silence_alert}
          onChange={(v) => void setSilence(v)}
          className="md:px-4"
        />
      )}
    </Card>
  );
}
